import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const SearchContext = createContext();

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return context;
};

export const SearchProvider = ({ children }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [currentViewedProfileId, setCurrentViewedProfileId] = useState(null);
  const [isFromSearch, setIsFromSearch] = useState(false);
  const viewedProfileRef = useRef(null);

  const updateSearchResults = useCallback((results) => {
    setSearchResults(results);
    setIsFromSearch(true);
  }, []);

  const clearSearchResults = useCallback(() => {
    setSearchResults([]);
    setCurrentViewedProfileId(null);
    setIsFromSearch(false);
    viewedProfileRef.current = null;
  }, []);

  const setViewedProfile = useCallback((profileId) => {
    // Only update if the profile ID is different to prevent unnecessary re-renders
    if (viewedProfileRef.current !== profileId) {
      viewedProfileRef.current = profileId;
      setCurrentViewedProfileId(profileId);
    }
  }, []);

  const getRemainingProfiles = useCallback(() => {
    if (!currentViewedProfileId || searchResults.length === 0) {
      return [];
    }
    return searchResults.filter(profile => profile.user_id !== currentViewedProfileId);
  }, [currentViewedProfileId, searchResults]);

  const value = {
    searchResults,
    currentViewedProfileId,
    isFromSearch,
    updateSearchResults,
    clearSearchResults,
    setViewedProfile,
    getRemainingProfiles
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
}; 